import { useState, useEffect } from 'react';
import { Trash2, Search, X, Calendar, DollarSign, User, Home, Phone, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { formatDateDDMMYYYY } from '../utils/format';

interface DeletedResident {
  id: string;
  original_resident_id: string;
  building: string;
  room_number: string;
  name: string;
  phone: string | null;
  identity_number: string | null;
  move_in_date: string | null;
  monthly_rent: number | null;
  electricity_rate: number | null;
  notes: string | null;
  deleted_at: string;
  deleted_by: string | null;
  deleted_by_email: string | null;
  deletion_reason: string | null;
  total_payments: number;
  total_paid_amount: number;
}

interface DeletedPayment {
  id: string;
  month: number;
  year: number;
  rent_amount: number | null;
  electricity_amount: number | null;
  total_amount: number | null;
  payment_date: string | null;
  payment_method: string | null;
  notes: string | null;
  original_created_at: string | null;
}

const MONTH_NAMES = [
  'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
];

export function DeletedResidentsHistory({ visible, onClose }: { visible: boolean; onClose: () => void }) {
  const [residents, setResidents] = useState<DeletedResident[]>([]);
  const [selectedResident, setSelectedResident] = useState<DeletedResident | null>(null);
  const [payments, setPayments] = useState<DeletedPayment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [buildingFilter, setBuildingFilter] = useState<string>('all');
  const [showDetail, setShowDetail] = useState(false);
  const [stats, setStats] = useState<any>(null);

  useEffect(() => {
    if (visible) {
      loadDeletedResidents();
      loadStats();
    }
  }, [visible, searchTerm, buildingFilter]);

  const loadDeletedResidents = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('v_deleted_residents_summary')
        .select('*')
        .order('deleted_at', { ascending: false });

      if (searchTerm) {
        query = query.or(`name.ilike.%${searchTerm}%,room_number.ilike.%${searchTerm}%,phone.ilike.%${searchTerm}%`);
      }

      if (buildingFilter !== 'all') {
        query = query.eq('building', buildingFilter);
      }

      const { data, error: queryError } = await query;

      if (queryError) throw queryError;
      setResidents((data || []) as DeletedResident[]);
    } catch (e: any) {
      setError(e.message || 'Gagal memuat data history penghuni terhapus');
      console.error('Error loading deleted residents:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data, error: queryError } = await supabase
        .rpc('get_deleted_residents_stats');

      if (queryError) throw queryError;
      setStats(data);
    } catch (e: any) {
      console.error('Error loading stats:', e);
    }
  };

  const loadResidentPayments = async (residentId: string) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: queryError } = await supabase
        .from('deleted_payments_history')
        .select('*')
        .eq('deleted_resident_history_id', residentId)
        .order('year', { ascending: false })
        .order('month', { ascending: false });

      if (queryError) throw queryError;
      setPayments((data || []) as DeletedPayment[]);
    } catch (e: any) {
      setError(e.message || 'Gagal memuat data pembayaran');
      console.error('Error loading payments:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (resident: DeletedResident) => {
    setSelectedResident(resident);
    setShowDetail(true);
    await loadResidentPayments(resident.id);
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null || amount === undefined) return 'Rp 0';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 dark:from-red-700 dark:to-red-800 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-3 rounded-xl">
              <Trash2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">History Penghuni Terhapus</h2>
              <p className="text-red-100 text-sm mt-1">Backup data penghuni dan pembayaran yang telah dihapus</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="p-6 bg-gray-50 dark:bg-gray-900 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <User className="w-8 h-8 text-red-600" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Total Penghuni Terhapus</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total_deleted_residents || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-blue-600" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Total Pembayaran</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total_deleted_payments || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <DollarSign className="w-8 h-8 text-green-600" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Total Nilai History</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{formatCurrency(stats.total_amount_history || 0)}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <Calendar className="w-8 h-8 text-purple-600" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Dihapus 30 Hari Terakhir</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.recent_deletions_30days || 0}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="p-6 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Cari nama, nomor kamar, atau nomor telepon..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>

            {/* Building Filter */}
            <select
              value={buildingFilter}
              onChange={(e) => setBuildingFilter(e.target.value)}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="all">Semua Gedung</option>
              <option value="Gajah 3">Gajah 3</option>
              <option value="Gajah 4">Gajah 4</option>
              <option value="Anduring">Anduring</option>
              <option value="Limau Manis">Limau Manis</option>
            </select>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          {loading && residents.length === 0 ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
              <p className="text-gray-600 dark:text-gray-400 mt-4">Memuat data...</p>
            </div>
          ) : residents.length === 0 ? (
            <div className="text-center py-12">
              <Trash2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400 text-lg">Tidak ada data history penghuni terhapus</p>
            </div>
          ) : (
            <div className="space-y-4">
              {residents.map((resident) => (
                <div
                  key={resident.id}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 space-y-3">
                        {/* Header Info */}
                        <div className="flex items-start gap-4">
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">{resident.name}</h3>
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-600 dark:text-gray-400">
                              <span className="flex items-center gap-1">
                                <Home className="w-4 h-4" />
                                {resident.building} - Kamar {resident.room_number}
                              </span>
                              {resident.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="w-4 h-4" />
                                  {resident.phone}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <span className="inline-block px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-semibold rounded-full">
                              Dihapus
                            </span>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {formatDateDDMMYYYY(resident.deleted_at)}
                            </p>
                          </div>
                        </div>

                        {/* Details Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Sewa/Bulan</p>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white">
                              {formatCurrency(resident.monthly_rent)}
                            </p>
                          </div>
                          
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Tanggal Masuk</p>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white">
                              {resident.move_in_date ? formatDateDDMMYYYY(resident.move_in_date) : '-'}
                            </p>
                          </div>
                          
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Total Pembayaran</p>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white">
                              {resident.total_payments} kali
                            </p>
                          </div>
                          
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Total Dibayar</p>
                            <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                              {formatCurrency(resident.total_paid_amount)}
                            </p>
                          </div>
                        </div>

                        {/* Deletion Info */}
                        {resident.deleted_by_email && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-700">
                            Dihapus oleh: <span className="font-medium">{resident.deleted_by_email}</span>
                          </div>
                        )}
                      </div>

                      {/* View Detail Button */}
                      <button
                        onClick={() => handleViewDetail(resident)}
                        className="ml-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                      >
                        <FileText className="w-4 h-4" />
                        Detail
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {showDetail && selectedResident && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Detail Header */}
            <div className="bg-gradient-to-r from-red-600 to-red-700 dark:from-red-700 dark:to-red-800 p-6 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-white">{selectedResident.name}</h3>
                <p className="text-red-100 text-sm mt-1">
                  {selectedResident.building} - Kamar {selectedResident.room_number}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowDetail(false);
                  setSelectedResident(null);
                  setPayments([]);
                }}
                className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Detail Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Resident Info */}
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Informasi Penghuni</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Nama Lengkap</p>
                    <p className="font-medium text-gray-900 dark:text-white">{selectedResident.name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">No. Telepon</p>
                    <p className="font-medium text-gray-900 dark:text-white">{selectedResident.phone || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">No. Identitas</p>
                    <p className="font-medium text-gray-900 dark:text-white">{selectedResident.identity_number || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Tanggal Masuk</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {selectedResident.move_in_date ? formatDateDDMMYYYY(selectedResident.move_in_date) : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Sewa per Bulan</p>
                    <p className="font-medium text-gray-900 dark:text-white">{formatCurrency(selectedResident.monthly_rent)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Tarif Listrik</p>
                    <p className="font-medium text-gray-900 dark:text-white">{formatCurrency(selectedResident.electricity_rate)}</p>
                  </div>
                </div>
                {selectedResident.notes && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-gray-500 dark:text-gray-400 text-sm">Catatan</p>
                    <p className="text-gray-900 dark:text-white text-sm mt-1">{selectedResident.notes}</p>
                  </div>
                )}
              </div>

              {/* Payment History */}
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  History Pembayaran ({payments.length})
                </h4>
                
                {payments.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <p className="text-gray-500 dark:text-gray-400">Tidak ada catatan pembayaran</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {payments.map((payment) => (
                      <div
                        key={payment.id}
                        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-sm transition-shadow"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {MONTH_NAMES[payment.month - 1]} {payment.year}
                            </p>
                            <div className="grid grid-cols-3 gap-4 mt-2 text-sm">
                              <div>
                                <p className="text-gray-500 dark:text-gray-400">Sewa</p>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {formatCurrency(payment.rent_amount)}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-500 dark:text-gray-400">Listrik</p>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {formatCurrency(payment.electricity_amount)}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-500 dark:text-gray-400">Total</p>
                                <p className="font-semibold text-green-600 dark:text-green-400">
                                  {formatCurrency(payment.total_amount)}
                                </p>
                              </div>
                            </div>
                            {payment.payment_date && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                Dibayar: {formatDateDDMMYYYY(payment.payment_date)}
                                {payment.payment_method && ` • ${payment.payment_method}`}
                              </p>
                            )}
                            {payment.notes && (
                              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 italic">
                                {payment.notes}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
